import React, { useEffect, useRef, useState, useCallback } from 'react'
import { clientStorage } from '../../../../shared/firebase'
import { nanoid } from 'nanoid'
import { ISafetyTeamDocument } from '../../shared/types'
import { changeSameName, getFileExtension } from '../../../../shared/helpers'
import DocumentsList from '../../../pages/reports/DocumentsList'
import { iLoadedFileInfo, enumActionButton, iFirebaseError } from '../../../../shared/interfaces'
import { LoadDocumentType } from '../../../../shared/interfaces'
import styles from './DocumentUpload.module.scss'

interface IDocumentUploadProps {
  documents: ISafetyTeamDocument[]
  recordId: string
  isEditing: boolean
  onChange: (documents: ISafetyTeamDocument[]) => void
  fileSizeLimit?: number // in bytes, default 10MB
  filesMaxCount?: number // optional limit on number of files
}

const documentsEqual = (a: ISafetyTeamDocument[], b: ISafetyTeamDocument[]): boolean => {
  if (a.length !== b.length) return false
  const aNames = new Set(a.map(d => d.storageName))
  const bNames = new Set(b.map(d => d.storageName))
  if (aNames.size !== bNames.size) return false
  for (const name of aNames) {
    if (!bNames.has(name)) return false
  }
  return true
}

const isFirebaseError = (error: unknown): error is iFirebaseError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string'
  )
}

interface IUploadTask {
  cancel(): boolean
  on(
    event: string,
    nextOrObserver?: (snapshot: { bytesTransferred: number; totalBytes: number }) => void,
    error?: (error: Error) => void,
    complete?: () => void
  ): void
}

const DocumentUpload: React.FC<IDocumentUploadProps> = ({
  documents,
  recordId,
  isEditing,
  onChange,
  fileSizeLimit = 10 * 1024 * 1024, // 10MB default
  filesMaxCount,
}) => {
  const [filesInfo, setFilesInfo] = useState<iLoadedFileInfo[]>([])
  const [tooMuchFilesError, setTooMuchFilesError] = useState<string>('')
  const [needFetch, setNeedFetch] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>('')
  const wasUnmounted = useRef<boolean>(false)
  const onChangeRef = useRef(onChange)
  const lastDocumentsRef = useRef<ISafetyTeamDocument[]>([])
  const lastNotifiedDocsRef = useRef<ISafetyTeamDocument[]>([])

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const removeItemUI = (storageName: string) => {
    if (wasUnmounted.current) return
    setFilesInfo((prev) => prev.filter((item) => item.storageName !== storageName))
  }

  const handleDeleteDocument = useCallback(async (doc: ISafetyTeamDocument) => {
    if (!doc.fullPath) {
      removeItemUI(doc.storageName)
      return
    }

    try {
      await clientStorage().child(doc.fullPath).delete()
      removeItemUI(doc.storageName)
    } catch (error) {
      if (isFirebaseError(error) && error.code === 'storage/object-not-found') {
        removeItemUI(doc.storageName)
      } else {
        console.error('Error deleting document:', error)
      }
    }
  }, [])

  useEffect(() => {
    if (!documentsEqual(documents, lastDocumentsRef.current)) {
      lastDocumentsRef.current = documents

      if (documents.length > 0) {
        const existingFiles: iLoadedFileInfo[] = documents.map((doc) => ({
          name: doc.name,
          storageName: doc.storageName,
          sizeInBytes: doc.sizeInBytes,
          fullPath: doc.fullPath,
          loaded: doc.sizeInBytes,
          isLoaded: true,
          inLoad: false,
          onDelete: isEditing ? () => handleDeleteDocument(doc) : undefined,
          actionButtonType: isEditing ? enumActionButton.DELETE : undefined,
        }))
        setFilesInfo(existingFiles)
      } else if (documents.length === 0 && !isEditing) {
        setFilesInfo([])
      }
    }
  }, [documents, isEditing, handleDeleteDocument])

  useEffect(() => {
    return () => {
      wasUnmounted.current = true
    }
  }, [])

  const inLoadHandleDeleteClick = async (item: iLoadedFileInfo, promise: IUploadTask) => {
    const { storageName } = item
    try {
      promise.cancel()
      removeItemUI(storageName)
    } catch (error) {
      if (isFirebaseError(error) && error.code === 'storage/canceled') {
        removeItemUI(storageName)
        return
      }
      const errorText = 'Upload error'
      setFilesInfo((prev) =>
        prev.map((itm) => {
          if (itm.storageName === storageName) {
            return {
              ...itm,
              inLoad: false,
              isCancel: true,
              onDelete: () => removeItemUI(storageName),
              errorText,
              actionButtonType: enumActionButton.DELETE,
            }
          }
          return itm
        })
      )
    }
  }

  const completeHandleDeleteClick = async (item: iLoadedFileInfo) => {
    if (!item.fullPath) {
      removeItemUI(item.storageName)
      return
    }
    try {
      await clientStorage().child(item.fullPath).delete()
      removeItemUI(item.storageName)
    } catch (error) {
      if (isFirebaseError(error) && error.code === 'storage/object-not-found') {
        removeItemUI(item.storageName)
      }
    }
  }

  const handleDownloadFileClick = async (info: iLoadedFileInfo) => {
    if (!info) return

    if (info.fullPath) {
      try {
        const url = await clientStorage().child(info.fullPath).getDownloadURL()
        const newWindow = window.open(url, '_blank')
        if (newWindow) newWindow.opener = null
      } catch (error) {
        console.error('Error getting download URL:', error)
      }
      return
    }

    if (info.file) {
      try {
        const blobUrl = URL.createObjectURL(info.file)
        const newWindow = window.open(blobUrl, '_blank')
        if (newWindow) newWindow.opener = null
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000)
      } catch (error) {
        console.error('Error opening local file:', error)
      }
    }
  }

  const startFetching = () => {
    const filesToSend = filesInfo.filter(
      (item) => !item.isCancel && !item.isLoaded && !item.inLoad
    )

    filesToSend.forEach((fileInfo) => {
      const { storageName, file } = fileInfo
      const fullPath = `safety-team/${recordId}/documents/${storageName}`
      const fileRef = clientStorage().child(fullPath)

      try {
        const uploadFilePromise = fileRef.put(file!)

        uploadFilePromise.on(
          'state_changed',
          (snapshot) => {
            const newLoaded = snapshot.bytesTransferred
            const onDelete = () => inLoadHandleDeleteClick(fileInfo, uploadFilePromise)

            setFilesInfo((prev) =>
              prev.map((itm) => {
                if (itm.storageName === storageName) {
                  return {
                    ...itm,
                    inLoad: true,
                    loaded: newLoaded,
                    sizeInBytes: snapshot.totalBytes,
                    onDelete,
                    actionButtonType: enumActionButton.CANCEL,
                  }
                }
                return itm
              })
            )
          },
          undefined,
          () => {
            setFilesInfo((prev) =>
              prev.map((itm) => {
                if (itm.storageName === storageName) {
                  const updatedItem = {
                    ...itm,
                    inLoad: false,
                    fullPath,
                    isLoaded: true,
                    actionButtonType: enumActionButton.DELETE,
                  }
                  const onDelete = () => completeHandleDeleteClick(updatedItem)
                  return {
                    ...updatedItem,
                    onDelete,
                  }
                }
                return itm
              })
            )
          }
        )
      } catch (e) {
        console.error('Upload error:', e)
      }
    })
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    const newFiles = e.target?.files
    setTooMuchFilesError('')

    if (!newFiles || newFiles.length === 0) return

    setFilesInfo((prev) => {
      const newFilesInfo = [...prev]

      for (let i = 0; i < newFiles.length; i++) {
        if (filesMaxCount && newFilesInfo.length >= filesMaxCount) {
          setTooMuchFilesError(`Select no more than ${filesMaxCount} files`)
          break
        }

        const notAvailableSize = fileSizeLimit && newFiles[i].size > fileSizeLimit
        const fileInfo: iLoadedFileInfo = {
          name: changeSameName(newFiles[i].name, newFilesInfo),
          storageName: `${nanoid()}.${getFileExtension(newFiles[i].name)}`,
          loaded: 0,
          sizeInBytes: newFiles[i].size,
          file: newFiles[i],
          isLoaded: false,
          inLoad: false,
        }

        if (notAvailableSize) {
          fileInfo.isCancel = true
          fileInfo.errorText = 'This file is too large'
          fileInfo.onDelete = () => removeItemUI(fileInfo.storageName)
          fileInfo.actionButtonType = enumActionButton.DELETE
        }

        newFilesInfo.push(fileInfo)
      }

      return newFilesInfo
    })

    setNeedFetch(true)
    setInputValue('')
  }

  useEffect(() => {
    const loadedDocs: ISafetyTeamDocument[] = filesInfo
      .filter((item) => item.isLoaded && item.fullPath)
      .map((item) => ({
        name: item.name,
        storageName: item.storageName,
        sizeInBytes: item.sizeInBytes,
        fullPath: item.fullPath!,
      }))

    if (!documentsEqual(loadedDocs, lastNotifiedDocsRef.current)) {
      lastNotifiedDocsRef.current = loadedDocs
      if (onChangeRef.current) {
        onChangeRef.current(loadedDocs)
      }
    }

    if (needFetch) {
      startFetching()
      setNeedFetch(false)
    }

  }, [filesInfo, needFetch])

  if (!isEditing) {
    if (documents.length === 0) {
      return <span className={styles.readonlyText}>No documents</span>
    }

    const documentNames = documents.map((doc) => doc.name).join(', ')

    const readonlyFiles: iLoadedFileInfo[] = documents.map((doc) => ({
      name: doc.name,
      storageName: doc.storageName,
      sizeInBytes: doc.sizeInBytes,
      fullPath: doc.fullPath,
      loaded: doc.sizeInBytes,
      isLoaded: true,
      inLoad: false,
    }))

    return (
      <div className={styles.readonlyDocuments} title={documentNames}>
        <div className={styles.documentsList}>
          <DocumentsList
            documents={readonlyFiles}
            loadType={LoadDocumentType.DOWNLOAD}
            hasDeleteButton={false}
            onDownloadFileClick={handleDownloadFileClick}
            columns={1}
            className={styles.documentsListWrapper}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.documentUpload}>
      {tooMuchFilesError && (
        <div className={styles.error}>{tooMuchFilesError}</div>
      )}

      {filesInfo.length > 0 && (
        <div className={styles.documentsList}>
          <DocumentsList
            documents={filesInfo}
            columns={1}
            loadType={LoadDocumentType.UPLOAD}
            hasDeleteButton={true}
            className={styles.documentsListWrapper}
          />
        </div>
      )}

      <label htmlFor={`document-upload-${recordId}`} className={styles.uploadButton}>
        <input
          type="file"
          id={`document-upload-${recordId}`}
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          value={inputValue}
        />
        <span>+ Attach</span>
      </label>
    </div>
  )
}

export default DocumentUpload
